/**
 * Permessi e ruoli.
 *
 * Stanno nel pacchetto condiviso perche client e server devono leggerli allo
 * stesso modo: il server per autorizzare, il client per non mostrare comandi
 * che l'utente non puo usare. Una sola definizione, nessuna possibilita che
 * le due divergano.
 *
 * L'assistente AI usa esattamente queste costanti: se il Mister non puo
 * cancellare una partita dall'interfaccia, non puo farlo nemmeno chiedendolo.
 */

export const PERMESSI = [
  // Piattaforma
  'piattaforma.societa.gestisci',
  'piattaforma.limiti.gestisci',
  // Societa
  'societa.dati.gestisci',
  'societa.utenze.gestisci',
  'societa.stagioni.gestisci',
  'societa.squadre.gestisci',
  // Anagrafiche
  'giocatore.leggi',
  'giocatore.gestisci',
  'giocatore.stato.gestisci',
  'tesseramento.gestisci',
  // Valutazioni
  'attributo.catalogo.gestisci',
  'misurazione.leggi',
  'misurazione.scrivi',
  'voto.leggi',
  'voto.scrivi',
  // Attivita
  'competizione.gestisci',
  'allenamento.leggi',
  'allenamento.gestisci',
  'presenza.scrivi',
  'partita.leggi',
  'partita.gestisci',
  'timer.opera',
  // Assistente
  'assistente.usa',
] as const;

export type Permesso = (typeof PERMESSI)[number];
export type Ruolo = 'super_admin' | 'admin_squadra' | 'mister' | 'dirigente';

/** I voti 1–10 sono il giudizio del Mister. */
const VOTI: readonly Permesso[] = ['voto.leggi', 'voto.scrivi'];

const VALUTAZIONI: readonly Permesso[] = ['misurazione.leggi', 'misurazione.scrivi', ...VOTI];

const PERMESSI_MISTER: readonly Permesso[] = [
  'giocatore.leggi',
  // Non 'giocatore.gestisci': l'anagrafica resta all'Admin Squadra. Ma lo
  // stato del giocatore, quello sportivo per competizione e le note cambiano
  // ogni settimana e li conosce chi sta sul campo.
  'giocatore.stato.gestisci',
  ...VALUTAZIONI,
  // Non 'competizione.gestisci': le competizioni sono della societa e poche,
  // e un nome sbagliato spezza le statistiche di tutte le squadre. Le
  // gestisce l'Admin Squadra; Mister e Dirigente scelgono fra le attive.
  'allenamento.leggi',
  'allenamento.gestisci',
  'presenza.scrivi',
  'partita.leggi',
  'partita.gestisci',
  'timer.opera',
  'assistente.usa',
];

/**
 * Matrice ruoli/permessi. L'Admin Squadra vede tutto della sua societa,
 * valutazioni comprese, ma non ne produce: scrive su anagrafiche, struttura e
 * utenze. Il Dirigente fa tutto cio che fa il Mister tranne i voti, a cui non
 * accede nemmeno in lettura: le misurazioni degli attributi invece le legge e
 * le scrive, perche molte le rileva lui sul campo. Derivarlo dal Mister
 * impedisce che le due liste divergano.
 */
export const PERMESSI_PER_RUOLO: Record<Ruolo, readonly Permesso[]> = {
  super_admin: ['piattaforma.societa.gestisci', 'piattaforma.limiti.gestisci'],

  admin_squadra: [
    'societa.dati.gestisci',
    'societa.utenze.gestisci',
    'societa.stagioni.gestisci',
    'societa.squadre.gestisci',
    'giocatore.leggi',
    'giocatore.gestisci',
    'giocatore.stato.gestisci',
    'tesseramento.gestisci',
    'attributo.catalogo.gestisci',
    'misurazione.leggi',
    'voto.leggi',
    'competizione.gestisci',
    'allenamento.leggi',
    'partita.leggi',
    'assistente.usa',
  ],

  mister: PERMESSI_MISTER,

  dirigente: PERMESSI_MISTER.filter((p) => !VOTI.includes(p)),
};

/** Ambito su cui un ruolo puo essere assegnato. */
export const AMBITO_DEL_RUOLO: Record<Ruolo, 'piattaforma' | 'societa' | 'squadra_stagione'> = {
  super_admin: 'piattaforma',
  admin_squadra: 'societa',
  mister: 'squadra_stagione',
  dirigente: 'squadra_stagione',
};

export interface Assegnazione {
  ruolo: Ruolo;
  /** Null per i ruoli di ambito societa. */
  squadraStagioneId: string | null;
}

/**
 * Verifica un permesso, eventualmente ristretto a una squadra-stagione.
 *
 * Passando `squadraStagioneId` si chiede: puo farlo *su quella squadra*?
 * I ruoli di ambito societa valgono ovunque nella societa; quelli di squadra
 * solo dove hanno un'assegnazione.
 */
export function puo(
  assegnazioni: readonly Assegnazione[],
  permesso: Permesso,
  squadraStagioneId?: string,
): boolean {
  return assegnazioni.some((a) => {
    if (!PERMESSI_PER_RUOLO[a.ruolo].includes(permesso)) return false;
    if (AMBITO_DEL_RUOLO[a.ruolo] !== 'squadra_stagione') return true;
    if (!squadraStagioneId) return false;
    return a.squadraStagioneId === squadraStagioneId;
  });
}

/** Squadre su cui l'utente ha almeno un permesso, per i filtri di elenco. */
export function squadreConPermesso(
  assegnazioni: readonly Assegnazione[],
  permesso: Permesso,
): string[] {
  const ids = new Set<string>();
  for (const a of assegnazioni) {
    if (!PERMESSI_PER_RUOLO[a.ruolo].includes(permesso)) continue;
    if (a.squadraStagioneId) ids.add(a.squadraStagioneId);
  }
  return [...ids];
}

/**
 * Dove vale un permesso: su tutta la societa se lo concede un ruolo di
 * ambito societa, altrimenti sulle sole squadre assegnate. Serve a filtrare
 * un elenco nella query invece di leggerlo tutto e scartare.
 */
export function ambitoDelPermesso(
  assegnazioni: readonly Assegnazione[],
  permesso: Permesso,
): 'societa' | string[] {
  const diSocieta = assegnazioni.some(
    (a) =>
      AMBITO_DEL_RUOLO[a.ruolo] === 'societa' && PERMESSI_PER_RUOLO[a.ruolo].includes(permesso),
  );
  return diSocieta ? 'societa' : squadreConPermesso(assegnazioni, permesso);
}
